import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Zap, Ship, Loader2, DollarSign, Leaf, AlertTriangle,
  ArrowRight, Gauge, Anchor, ChevronRight, Sparkles
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip
} from 'recharts';

import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue
} from '@/components/ui/select';

const COST_COLORS = ['#0A2463', '#3B82F6', '#F59E0B', '#E11D48', '#16A34A', '#8B5CF6'];

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function DealAnalyzerPage() {
  const [vessels, setVessels] = useState([]);
  const [ports, setPorts] = useState({ loading: [], discharge: [] });
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [form, setForm] = useState({
    port_load: 'US Gulf',
    port_discharge: 'NE Asia',
    cargo_mt: '65000',
    vessel_id: '',
    vessel_name: '',
    speed_knots: '14.5',
    charter_rate_daily: '80000',
    laytime_hours: '72',
    demurrage_rate_daily: '45000',
    carbon_price_per_ton: '80',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [vesselRes, portsRes] = await Promise.all([
          api.get('/deals/vessels'),
          api.get('/deals/ports'),
        ]);
        setVessels(vesselRes.data);
        setPorts(portsRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleVesselSelect = (e) => {
    const vid = e.target.value;
    handleChange('vessel_id', vid);
    const v = vessels.find(x => x.id === vid);
    if (v) handleChange('vessel_name', v.name);
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        cargo_mt: parseFloat(form.cargo_mt) || 65000,
        speed_knots: parseFloat(form.speed_knots) || 14.5,
        charter_rate_daily: parseFloat(form.charter_rate_daily) || 80000,
        laytime_hours: parseFloat(form.laytime_hours) || 72,
        demurrage_rate_daily: parseFloat(form.demurrage_rate_daily) || 45000,
        carbon_price_per_ton: parseFloat(form.carbon_price_per_ton) || 80,
      };
      const { data } = await api.post('/deals/analyze', payload);
      setResult(data);
    } catch (e) {
      console.error(e);
    }
    finally { setAnalyzing(false); }
  };

  const costPieData = result ? [
    { name: 'Freight', value: result.freight_cost },
    { name: 'Carbon', value: result.carbon_cost },
    { name: 'Terminal Fees', value: result.secondary_costs?.terminal_fees || 0 },
    { name: 'Demurrage', value: result.demurrage_exposure },
    { name: 'Other Costs', value: (result.total_secondary_costs || 0) - (result.secondary_costs?.terminal_fees || 0) },
    { name: 'Insurance', value: result.insurance_cost },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="deal-analyzer-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Deal Analyzer</h1>
        <p className="text-sm text-slate-500 mt-1">Pre-trade decision support — projected cost breakdown with emissions impact</p>
      </div>

      {/* Input Form */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#0A2463]" />Trade Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <Field label="Loading Port">
              <Select value={form.port_load} onValueChange={(v) => handleChange('port_load', v)} data-testid="select-port-load">
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="input-port-load">
                  <SelectValue placeholder="Select port" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(
                    ports.loading.reduce((groups, p) => {
                      (groups[p.region] = groups[p.region] || []).push(p);
                      return groups;
                    }, {})
                  ).map(([region, regionPorts]) => (
                    <SelectGroup key={region}>
                      <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">{region}</SelectLabel>
                      {regionPorts.map(p => (
                        <SelectItem key={p.id} value={p.name} className="text-xs">{p.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Discharge Port">
              <Select value={form.port_discharge} onValueChange={(v) => handleChange('port_discharge', v)} data-testid="select-port-discharge">
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="input-port-discharge">
                  <SelectValue placeholder="Select port" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(
                    ports.discharge.reduce((groups, p) => {
                      (groups[p.region] = groups[p.region] || []).push(p);
                      return groups;
                    }, {})
                  ).map(([region, regionPorts]) => (
                    <SelectGroup key={region}>
                      <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">{region}</SelectLabel>
                      {regionPorts.map(p => (
                        <SelectItem key={p.id} value={p.name} className="text-xs">{p.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cargo (MT)">
              <Input
                type="number"
                value={form.cargo_mt}
                onChange={(e) => handleChange('cargo_mt', e.target.value)}
                className="rounded-sm h-9 text-xs font-mono"
                data-testid="input-cargo-mt"
              />
            </Field>
            <Field label="Vessel">
              <select
                value={form.vessel_id}
                onChange={handleVesselSelect}
                className="w-full h-9 rounded-sm border border-slate-200 bg-white px-2 text-xs"
                data-testid="select-vessel"
              >
                <option value="">Custom / TBN</option>
                {vessels.map(v => (
                  <option key={v.id} value={v.id}>{v.name} ({v.imo})</option>
                ))}
              </select>
            </Field>
            <Field label="Speed (kn)">
              <Input
                type="number"
                value={form.speed_knots}
                onChange={(e) => handleChange('speed_knots', e.target.value)}
                className="rounded-sm h-9 text-xs font-mono"
                data-testid="input-speed"
              />
            </Field>
          </div>

          <Separator className="my-3" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Charter Rate ($/day)">
              <Input
                type="number"
                value={form.charter_rate_daily}
                onChange={(e) => handleChange('charter_rate_daily', e.target.value)}
                className="rounded-sm h-9 text-xs font-mono"
                data-testid="input-charter-rate"
              />
            </Field>
            <Field label="Laytime (hours)">
              <Input
                type="number"
                value={form.laytime_hours}
                onChange={(e) => handleChange('laytime_hours', e.target.value)}
                className="rounded-sm h-9 text-xs font-mono"
                data-testid="input-laytime"
              />
            </Field>
            <Field label="Demurrage Rate ($/day)">
              <Input
                type="number"
                value={form.demurrage_rate_daily}
                onChange={(e) => handleChange('demurrage_rate_daily', e.target.value)}
                className="rounded-sm h-9 text-xs font-mono"
                data-testid="input-demurrage-rate"
              />
            </Field>
            <Field label="Carbon Price ($/ton)">
              <Input
                type="number"
                value={form.carbon_price_per_ton}
                onChange={(e) => handleChange('carbon_price_per_ton', e.target.value)}
                className="rounded-sm h-9 text-xs font-mono"
                data-testid="input-carbon-price"
              />
            </Field>
          </div>

          <div className="mt-4">
            <Button
              onClick={handleAnalyze}
              disabled={analyzing || !form.port_load || !form.port_discharge}
              data-testid="analyze-deal-button"
              className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white px-8"
            >
              {analyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />Analyze Deal
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {analyzing && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0A2463]" />
            <p className="text-sm text-slate-500 mt-3">Running cost projections & emissions analysis...</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4" data-testid="deal-result">
          {/* Route Header */}
          <Card className="bg-[#0A2463]/[0.03] border border-[#0A2463]/20 rounded-sm shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-[#0A2463]">
                    <Anchor className="h-4 w-4" />
                    {result.port_load}
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    {result.port_discharge}
                  </div>
                  <Badge variant="outline" className="rounded-sm text-[9px]">{result.distance_nm?.toLocaleString()} NM</Badge>
                  <Badge variant="outline" className="rounded-sm text-[9px]">{result.vessel_name}</Badge>
                </div>
                <div className="text-right">
                  <p className="font-mono text-2xl font-bold text-slate-900">${result.true_total_cost?.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">True total cost · ${result.cost_per_mmbtu?.toFixed(4)}/MMBtu</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Freight', value: `$${(result.freight_cost || 0).toLocaleString()}`, icon: Ship, color: 'bg-[#0A2463]' },
              { label: 'Carbon Cost', value: `$${(result.carbon_cost || 0).toLocaleString()}`, icon: Leaf, color: 'bg-[#16A34A]' },
              { label: 'Demurrage Exp.', value: `$${(result.demurrage_exposure || 0).toLocaleString()}`, icon: AlertTriangle, color: 'bg-[#F59E0B]' },
              { label: 'Hidden Costs', value: `$${(result.total_hidden_costs || 0).toLocaleString()}`, icon: DollarSign, color: 'bg-[#E11D48]' },
              { label: 'CO2e', value: `${(result.emissions?.total_co2e_tons || 0).toLocaleString()} t`, icon: Gauge, color: 'bg-[#007AFF]' },
              { label: 'Transit', value: `${result.transit_days} days`, icon: Ship, color: 'bg-slate-500' },
            ].map((kpi) => (
              <Card key={kpi.label} className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.2em] font-semibold text-slate-500">{kpi.label}</p>
                      <p className="font-mono text-base font-bold text-slate-900 mt-0.5">{kpi.value}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-sm flex items-center justify-center ${kpi.color}`}>
                      <kpi.icon className="h-3 w-3 text-white stroke-[1.5px]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Cost Pie Chart */}
            <div className="lg:col-span-4">
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none h-full">
                <CardHeader className="pb-1">
                  <CardTitle className="text-sm font-semibold text-slate-800">Cost Allocation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={costPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {costPieData.map((_, i) => (
                            <Cell key={i} fill={COST_COLORS[i % COST_COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }}
                          formatter={(v) => `$${v.toLocaleString()}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 justify-center">
                    {costPieData.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: COST_COLORS[i % COST_COLORS.length] }} />
                        <span className="text-[9px] text-slate-500">{d.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cost Breakdown Detail */}
            <div className="lg:col-span-4">
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none h-full">
                <CardHeader className="pb-1">
                  <CardTitle className="text-sm font-semibold text-slate-800">Detailed Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-1.5">
                  {[
                    { label: 'Freight / Charter', value: result.freight_cost, bold: true },
                    { label: 'Carbon (EU ETS)', value: result.carbon_cost },
                    { label: 'Demurrage Exposure', value: result.demurrage_exposure },
                    { label: 'Insurance', value: result.insurance_cost },
                    ...Object.entries(result.secondary_costs || {}).map(([k, v]) => ({
                      label: k.replace(/_/g, ' '), value: v, small: true,
                    })),
                  ].map(item => (
                    <div key={item.label} className={`flex items-center justify-between ${item.small ? 'pl-3' : ''}`}>
                      <span className={`text-xs ${item.bold ? 'font-medium text-slate-800' : item.small ? 'text-slate-400' : 'text-slate-600'}`}>
                        {item.small && <ChevronRight className="h-2.5 w-2.5 inline mr-0.5" />}
                        {item.label}
                      </span>
                      <span className={`text-xs font-mono ${item.bold ? 'font-bold text-slate-900' : 'text-slate-600'}`}>
                        ${(item.value || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                  <Separator className="my-1.5" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900">True Total</span>
                    <span className="text-sm font-mono font-bold text-[#0A2463]">${result.true_total_cost?.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Emissions Impact */}
            <div className="lg:col-span-4">
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none h-full">
                <CardHeader className="pb-1">
                  <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Leaf className="h-4 w-4 text-green-600" />Emissions Impact
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-2">
                  {[
                    { label: 'CO2 Combustion', value: `${result.emissions?.co2_combustion_tons?.toLocaleString()} t` },
                    { label: 'CO2 Boil-off', value: `${result.emissions?.co2_boiloff_tons?.toLocaleString()} t` },
                    { label: 'CH4 Slip (CO2e)', value: `${result.emissions?.methane_slip_co2e_tons?.toLocaleString()} t` },
                    { label: 'N2O (CO2e)', value: `${result.emissions?.n2o_co2e_tons?.toLocaleString()} t` },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between">
                      <span className="text-xs text-slate-600">{item.label}</span>
                      <span className="text-xs font-mono text-slate-700">{item.value}</span>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900">Total CO2e</span>
                    <span className="text-sm font-mono font-bold text-green-700">{result.emissions?.total_co2e_tons?.toLocaleString()} t</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="px-2 py-1.5 bg-slate-50 border border-slate-100 rounded-sm">
                      <p className="text-[9px] uppercase text-slate-500">EEOI</p>
                      <p className="text-xs font-mono font-bold text-slate-800">{result.emissions?.eeoi?.toFixed(2)}</p>
                    </div>
                    <div className="px-2 py-1.5 bg-slate-50 border border-slate-100 rounded-sm">
                      <p className="text-[9px] uppercase text-slate-500">CO2e/MMBtu</p>
                      <p className="text-xs font-mono font-bold text-slate-800">{result.emissions?.co2e_per_mmbtu?.toFixed(6)}</p>
                    </div>
                  </div>
                  <div className="px-2 py-1.5 bg-green-50 border border-green-100 rounded-sm">
                    <p className="text-[9px] uppercase text-green-700">Carbon Cost Impact</p>
                    <p className="text-xs font-mono font-bold text-green-800">${result.carbon_cost?.toLocaleString()} at ${result.emissions?.carbon_price_per_ton}/tCO2e</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* AI Narrative */}
          {result.ai_narrative && (
            <Card className="bg-white border border-[#0A2463]/20 rounded-sm shadow-none" data-testid="deal-ai-narrative">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-sm bg-[#0A2463]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="h-4 w-4 text-[#0A2463]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[#0A2463] mb-1.5">AI Deal Analysis</p>
                    <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {result.ai_narrative}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Voyage Details */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-semibold text-slate-800">Voyage Details</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {[
                  { label: 'Distance', value: `${result.distance_nm?.toLocaleString()} NM` },
                  { label: 'Transit', value: `${result.transit_days} days` },
                  { label: 'Total Voyage', value: `${result.total_voyage_days} days` },
                  { label: 'Fuel Used', value: `${result.fuel_consumption_tons?.toLocaleString()} t` },
                  { label: 'Cargo', value: `${result.cargo_mt?.toLocaleString()} MT` },
                  { label: 'MMBtu', value: result.cargo_mmbtu?.toLocaleString() },
                ].map(d => (
                  <div key={d.label}>
                    <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{d.label}</p>
                    <p className="text-xs font-mono font-bold text-slate-800 mt-0.5">{d.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
